/**
 * A turn's timestamp, made into the way you play it.
 *
 * The timecode was already the only thing on a turn that says *where* in the recording it is, so
 * playing from there is the same gesture as reading it: the label and the glyph are **one** hit
 * target, 24px tall so a thumb can find it, and the glyph is always drawn rather than appearing on
 * hover — an affordance that only exists under a pointer does not exist on a touch screen, and a
 * page of timestamps that each hide a control is a page nobody discovers.
 *
 * ── The glyph is small on purpose ────────────────────────────────────────────
 * A 16px box holding a 10px icon, transparent at rest. It rides beside a 11px timecode down a
 * column of prose, and anything bigger reads as a button the page is *about* rather than a mark
 * beside a timestamp. The hit target is the pressable row around it, not the box: the box is what
 * the eye sees, the row is what the finger gets. Hover and keyboard focus fill the box and darken
 * the icon, which is the whole of its pressed-ness.
 *
 * The glyph is a pause on the turn currently *sounding*, because that is what the press would do;
 * the active turn paused shows a play glyph, still in the accent that says the playhead is here.
 * Everything about which turn that is comes from the one clock the viewer reads.
 */

import { PauseIcon, PlayIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { PLAYBACK_UNSUPPORTED_FORMAT_MESSAGE } from "../lib/transcript-playback";
import { PlaybackUnsupportedTooltip } from "./transcript-playback-tooltip";

export type TranscriptTurnPlayButtonProps = {
  /** The turn's own timecode, formatted exactly as it reads without a player. */
  timecode: string;
  /** This turn holds the playhead. */
  isActive: boolean;
  /** ...and the recording is sounding, so the press pauses instead of starting. */
  isSounding: boolean;
  /**
   * This browser cannot decode the recording's audio. The glyph stays — the timestamp is still a
   * timestamp — but it is inert, and the sentence beside it says what would change that.
   */
  isUnsupported: boolean;
  onPress: () => void;
};

export function TranscriptTurnPlayButton({
  timecode,
  isActive,
  isSounding,
  isUnsupported,
  onPress,
}: TranscriptTurnPlayButtonProps) {
  const Glyph = isSounding ? PauseIcon : PlayIcon;
  return (
    <PlaybackUnsupportedTooltip enabled={isUnsupported}>
      <button
        type="button"
        data-testid="transcript-turn-play"
        data-active={isActive || undefined}
        disabled={isUnsupported}
        aria-label={
          isUnsupported
            ? PLAYBACK_UNSUPPORTED_FORMAT_MESSAGE
            : isSounding
              ? `Pause at ${timecode}`
              : `Play from ${timecode}`
        }
        className={cn(
          // The timecode and the box are one press, and the row is what carries the 24px height —
          // padding here would inflate the box, which has to stay 16px.
          "group flex min-h-6 items-center gap-2 focus-visible:outline-2 focus-visible:outline-ring focus-visible:outline-offset-1",
          isUnsupported
            ? // A disabled control swallows the pointer along with the tooltip it would open, so
              // the wrapper takes the hover instead.
              "pointer-events-none cursor-not-allowed opacity-60"
            : "cursor-pointer"
        )}
        onClick={onPress}
      >
        <span
          data-testid="transcript-timecode"
          className={cn(
            "text-[11px] tabular-nums",
            isActive ? "font-medium text-primary" : "text-muted-foreground"
          )}
        >
          {timecode}
        </span>
        <span
          data-testid="transcript-turn-play-box"
          className={cn(
            "flex size-4 shrink-0 items-center justify-center",
            isUnsupported ? null : "group-focus-visible:bg-muted group-hover:bg-muted"
          )}
        >
          <Glyph
            data-testid="transcript-turn-play-glyph"
            data-glyph={isSounding ? "pause" : "play"}
            className={cn(
              "size-2.5 shrink-0",
              // The accent outlives the pointer: on the turn holding the playhead the glyph stays
              // the colour that says so, rather than going dark under a hover like any other.
              isActive ? "text-primary" : "text-neutral-dim",
              isUnsupported || isActive ? null : "group-hover:text-foreground"
            )}
            aria-hidden
          />
        </span>
      </button>
    </PlaybackUnsupportedTooltip>
  );
}
